import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { fetchSuppliersData, downloadSuppliersData } from "./api";

function useStickyState(defaultValue, key) {
  const [value, setValue] = useState(() => {
    try {
      const stickyValue = window.localStorage.getItem(key);
      return stickyValue !== null ? JSON.parse(stickyValue) : defaultValue;
    } catch (e) {
      console.warn("Error reading from localStorage", e);
      return defaultValue;
    }
  });

  useEffect(() => {
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
    } catch (e) {
      console.warn("Error writing to localStorage", e);
    }
  }, [key, value]);

  return [value, setValue];
}

const iconVisible = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="32"
    height="32"
    fill="#f5f5f5"
    viewBox="0 0 256 256"
  >
    <path d="M247.31,124.76c-.35-.79-8.82-19.58-27.65-38.41C194.57,61.26,162.88,48,128,48S61.43,61.26,36.34,86.35C17.51,105.18,9,124,8.69,124.76a8,8,0,0,0,0,6.5c.35.79,8.82,19.57,27.65,38.4C61.43,194.74,93.12,208,128,208s66.57-13.26,91.66-38.34c18.83-18.83,27.3-37.61,27.65-38.4A8,8,0,0,0,247.31,124.76ZM128,168a40,40,0,1,1,40-40A40,40,0,0,1,128,168Z"></path>
  </svg>
);
const iconHidden = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="32"
    height="32"
    fill="#f5f5f5"
    viewBox="0 0 256 256"
  >
    <path d="M96.68,57.87a4,4,0,0,1,2.08-6.6A130.13,130.13,0,0,1,128,48c34.88,0,66.57,13.26,91.66,38.35,18.83,18.83,27.3,37.62,27.65,38.41a8,8,0,0,1,0,6.5c-.35.79-8.82,19.57-27.65,38.4q-4.28,4.26-8.79,8.07a4,4,0,0,1-5.55-.36ZM213.92,210.62a8,8,0,1,1-11.84,10.76L180,197.13A127.21,127.21,0,0,1,128,208c-34.88,0-66.57-13.26-91.66-38.34C17.51,150.83,9,132.05,8.69,131.26a8,8,0,0,1,0-6.5C9,124,17.51,105.18,36.34,86.35a135,135,0,0,1,25-19.78L42.08,45.38A8,8,0,1,1,53.92,34.62Zm-65.49-48.25-52.69-58a40,40,0,0,0,52.69,58Z"></path>
  </svg>
);
const iconDownload = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    fill="#ffffff"
    viewBox="0 0 256 256"
  >
    <path d="M222,144v64a6,6,0,0,1-6,6H40a6,6,0,0,1-6-6V144a6,6,0,0,1,12,0v58H210V144a6,6,0,0,1,12,0Zm-98.24,4.24a6,6,0,0,0,8.48,0l40-40a6,6,0,0,0-8.48-8.48L134,129.51V32a6,6,0,0,0-12,0v97.51L92.24,99.76a6,6,0,0,0-8.48,8.48Z"></path>
  </svg>
);



// Helper per gestire i null
const formatCell = (val) => {
  if (val === null || val === undefined || val === "") return "N/A";
  return val;
};

// CONFIGURAZIONE CENTRALIZZATA COLONNE
const SUPPLIERS_COLUMNS = {
  id: {
    label: "Supplier ID",
    sortKey: "SUPPLIER ID",
    render: (row) => formatCell(row.id),
  },
  name: {
    label: "Supplier Name",
    sortKey: "SUPPLIER NAME",
    render: (row) => formatCell(row.name),
  },
  description: {
    label: "Supplier Description",
    sortKey: "SUPPLIER DESCRIPTION",
    render: (row) => formatCell(row.description),
  },
};

// Componente Paginazione
const Pagination = ({ currentPage, totalPages, onPageChange }) => {
  const currentPageUI = currentPage + 1;

  const renderControls = () => {
    if (totalPages <= 1) return null;
    const buttons = [];
    const maxVisibleButtons = 5;

    buttons.push(
      <button
        key="prev"
        className="pagination-btn"
        onClick={() => onPageChange(currentPageUI - 1)}
        disabled={currentPageUI === 1}
      >
        Previous
      </button>
    );

    let startPage, endPage;
    if (totalPages <= maxVisibleButtons + 2) {
      startPage = 1;
      endPage = totalPages;
    } else {
      if (currentPageUI <= 3) {
        startPage = 1;
        endPage = 5;
      } else if (currentPageUI >= totalPages - 2) {
        startPage = totalPages - 4;
        endPage = totalPages;
      } else {
        startPage = currentPageUI - 2;
        endPage = currentPageUI + 2;
      }
    }

    if (startPage > 1) {
      buttons.push(
        <button
          key={1}
          className="pagination-btn"
          onClick={() => onPageChange(1)}
        >
          1
        </button>
      );
      if (startPage > 2) {
        buttons.push(
          <span key="start-ellipsis" className="pagination-ellipsis">
            ...
          </span>
        );
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      buttons.push(
        <button
          key={i}
          className={`pagination-btn ${currentPageUI === i ? "active" : ""}`}
          onClick={() => onPageChange(i)}
        >
          {i}
        </button>
      );
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        buttons.push(
          <span key="end-ellipsis" className="pagination-ellipsis">
            ...
          </span>
        );
      }
      buttons.push(
        <button
          key={totalPages}
          className="pagination-btn"
          onClick={() => onPageChange(totalPages)}
        >
          {totalPages}
        </button>
      );
    }

    buttons.push(
      <button
        key="next"
        className="pagination-btn"
        onClick={() => onPageChange(currentPageUI + 1)}
        disabled={currentPageUI === totalPages}
      >
        Next
      </button>
    );

    return buttons;
  };

  return <div className="pagination-container">{renderControls()}</div>;
};

function SuppliersPage() {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

  // Stato Dati
  const [tableData, setTableData] = useState([]);
  const [pagination, setPagination] = useState({
    current_page: 0,
    total_pages: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // === Stato per l'ordinamento ===
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });



  const [isDownloading, setIsDownloading] = useState(false);
  const [isColumnDropdownOpen, setIsColumnDropdownOpen] = useState(false);

  const columnColsRef = useRef(null);

  // Ordine iniziale
  const [columnOrder, setColumnOrder] = useStickyState(
    Object.keys(SUPPLIERS_COLUMNS),
    "suppliers_columnOrder"
  );

  // Visibilità iniziale
  const [columnVisibility, setColumnVisibility] = useStickyState(
    Object.keys(SUPPLIERS_COLUMNS).reduce((acc, key) => {
      acc[key] = true;
      return acc;
    }, {}),
    "suppliers_columnVisibility"
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        columnColsRef.current &&
        !columnColsRef.current.contains(event.target)
      ) {
        setIsColumnDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);



  // Funzione per gestire il riposizionamento tramite Drag & Drop
  const handleOnDrop = (draggedIndex, targetIndex) => {
    if (draggedIndex === targetIndex) return;

    const newOrder = [...columnOrder];
    const draggedItem = newOrder[draggedIndex];

    // Rimuove l'elemento dalla vecchia posizione e lo inserisce nella nuova
    newOrder.splice(draggedIndex, 1);
    newOrder.splice(targetIndex, 0, draggedItem);

    setColumnOrder(newOrder);
  };

  // === Funzioni di Gestione Ordinamento ===
  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    } else if (sortConfig.key === key && sortConfig.direction === "desc") {
      direction = null; // Reset
    }
    setSortConfig({ key: direction ? key : null, direction });
    setPagination((prev) => ({ ...prev, current_page: 0 }));
  };

  const getSortIndicator = (key) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === "asc" ? " ▲" : " ▼";
  };

  // FUNZIONE DOWNLOAD
  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      const apiSortKey = sortConfig.key
        ? SUPPLIERS_COLUMNS[sortConfig.key]?.sortKey
        : undefined;

      const downloadParams = {
        order_by: apiSortKey,
        order: sortConfig.direction || undefined,
      };
      await downloadSuppliersData(downloadParams, "Suppliers_Data.xlsx");
    } catch (error) {
      console.error(error);
      alert("Error downloading file.");
    } finally {
      setIsDownloading(false);
    }
  };

  // === 4. Caricamento Dati Aggiornato ===
  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const apiSortKey = sortConfig.key
        ? SUPPLIERS_COLUMNS[sortConfig.key]?.sortKey
        : undefined;

      const params = {
        page_n: pagination.current_page,
        page_size: 50,
        order_by: apiSortKey,
        order: sortConfig.direction || undefined,
      };

      const response = await fetchSuppliersData(params);

      if (response && response.result) {
        setTableData(response.result);
        setPagination((prev) => ({
          ...prev,
          current_page: response.pagination.current_page,
          total_pages: response.pagination.total_pages,
        }));
      } else {
        setTableData([]);
      }
    } catch (err) {
      console.error("Errore caricamento Suppliers:", err);
      setError("Failed to load data from server.");
    } finally {
      setLoading(false);
    }
  }, [pagination.current_page, sortConfig]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handlePageChange = (newPageUI) => {
    setPagination((prev) => ({ ...prev, current_page: newPageUI - 1 }));
    document.querySelector(".main-table-container")?.scrollTo(0, 0);
  };

  return (
    <>
      <div className="sidebar-container">
        <button
          id="sidebar-toggle"
          className={`sidebar-toggle-btn ${isSidebarOpen ? "open" : ""}`}
          onClick={toggleSidebar}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="28"
            height="28"
            fill="#FFFFFF"
            viewBox="0 0 256 256"
          >
            <path d="M181.66,133.66l-80,80a8,8,0,0,1-11.32-11.32L164.69,128,90.34,53.66a8,8,0,0,1,11.32-11.32l80,80A8,8,0,0,1,181.66,133.66Z"></path>
          </svg>
        </button>

        <nav
          id="sidebar-menu"
          className={`sidebar ${isSidebarOpen ? "open" : ""}`}
        >
          <ul>
            <li>
              <Link to="/master-data">Product Master Data</Link>
            </li>
            <li>
              <Link to="/sku-lt">SKU & LT</Link>
            </li>
            <li>
              <Link to="/suppliers">Suppliers</Link>
            </li>
            <li>
              <Link to="/quotations-demand">Adjusted Demand</Link>
            </li>
            <li>
              <Link to="/bookings">Bookings (Sales Orders)</Link>
            </li>
            <li>
              <Link to="/inventory-snapshot">Inventory Snapshots</Link>
            </li>
            <li>
              <Link to="/purchase-orders">Purchase Orders</Link>
            </li>
          </ul>
        </nav>
      </div>

      <header>
        <h1>Suppliers</h1>
        <div className="header-buttons">
          <Link to="/" className="header-btn">
            GLOBAL VIEW
          </Link>
        </div>
      </header>

      <main className="dashboard-container">
        <div className="filter-section">
          <div
            className="filter-actions"
            style={{
              marginLeft: "auto",
              display: "flex",
              alignItems: "center",
            }}
          >
            <button
              onClick={handleDownload}
              className={`filter-btn download-btn ${
                isDownloading ? "loading" : ""
              }`}
              disabled={isDownloading}
              title="Download CSV"
            >
              <div className="btn-icon">{iconDownload}</div>
              <span>Download</span>
            </button>

            <div
              className="column-visibility-container"
              style={{ marginLeft: "10px" }}
              ref={columnColsRef}
            >
              <button
                className="toggle-btn column-toggle-btn"
                onClick={() => setIsColumnDropdownOpen(!isColumnDropdownOpen)}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  fill="#f5f5f5"
                  viewBox="0 0 256 256"
                >
                  <path d="M84,52V92H28a4,4,0,0,1-4-4V64A16,16,0,0,1,40,48H80A4,4,0,0,1,84,52Zm16,152a4,4,0,0,0,4,4h48a4,4,0,0,0,4-4V164H100ZM24,168v24a16,16,0,0,0,16,16H80a4,4,0,0,0,4-4V164H28A4,4,0,0,0,24,168Zm0-56v32a4,4,0,0,0,4,4H84V108H28A4,4,0,0,0,24,112ZM152,48H104a4,4,0,0,0-4,4V92h56V52A4,4,0,0,0,152,48Zm76,60H172v40h56a4,4,0,0,0,4-4V112A4,4,0,0,0,228,108ZM100,148h56V108H100ZM216,48H176a4,4,0,0,0-4,4V92h56a4,4,0,0,0,4-4V64A16,16,0,0,0,216,48Zm12,116H172v40a4,4,0,0,0,4,4h40a16,16,0,0,0,16-16V168A4,4,0,0,0,228,164Z"></path>
                </svg>
                <span>COLUMNS</span>
              </button>
              {isColumnDropdownOpen && (
                <div className="column-dropdown show">
                  {columnOrder.map((key, index) => (
                    <div
                      key={key}
                      className="column-dropdown-item"
                      draggable="true"
                      onDragStart={(e) => {
                        e.dataTransfer.setData("draggedIdx", index);
                        e.currentTarget.classList.add("dragging");
                      }}
                      onDragEnd={(e) => {
                        e.currentTarget.classList.remove("dragging");
                      }}
                      onDragOver={(e) => {
                        e.preventDefault();
                        e.currentTarget.classList.add("drag-over");
                      }}
                      onDragLeave={(e) => {
                        e.currentTarget.classList.remove("drag-over");
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        e.currentTarget.classList.remove("drag-over");
                        const draggedIdx = e.dataTransfer.getData("draggedIdx");
                        handleOnDrop(parseInt(draggedIdx), index);
                      }}
                      style={{ cursor: "grab" }}
                    >
                      <span style={{ flex: 1 }}>
                        {SUPPLIERS_COLUMNS[key].label}
                      </span>

                      <div style={{ display: "flex", gap: "5px" }}>
                        <div
                          className="column-visibility-icon"
                          onClick={() =>
                            setColumnVisibility((prev) => ({
                              ...prev,
                              [key]: !prev[key],
                            }))
                          }
                          style={{
                            cursor: "pointer",
                          }}
                        >
                          {columnVisibility[key] ? iconVisible : iconHidden}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="main-table-container full-page-table">
          <table className="main-table master-data-table">
            <thead>
              <tr>
                {columnOrder.map((key) => {
                  if (!columnVisibility[key]) return null;
                  const colDef = SUPPLIERS_COLUMNS[key];

                  return (
                    <th
                      key={key}
                      onClick={() => requestSort(key)}
                      style={{ cursor: "pointer", userSelect: "none" }}
                    >
                      {colDef.label}
                      {getSortIndicator(key)}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody
              style={{
                opacity: loading ? 0.5 : 1,
                transition: "opacity 0.2s ease-in-out",
              }}
            >
              {/* Loading State */}
              {loading && tableData.length === 0 && (
                <tr>
                  <td
                    colSpan={
                      columnOrder.filter((k) => columnVisibility[k]).length
                    }
                    style={{ textAlign: "center", padding: "2rem" }}
                  >
                    Loading data...
                  </td>
                </tr>
              )}

              {/* Error State */}
              {error && (
                <tr>
                  <td
                    colSpan={
                      columnOrder.filter((k) => columnVisibility[k]).length
                    }
                    style={{
                      color: "red",
                      textAlign: "center",
                      padding: "2rem",
                    }}
                  >
                    {error}
                  </td>
                </tr>
              )}

              {/* Data Rows */}
              {tableData.map((row, index) => (
                <tr key={`${row.id}-${index}`}>
                  {columnOrder.map((key) => {
                    if (!columnVisibility[key]) return null;
                    const colDef = SUPPLIERS_COLUMNS[key];
                    return <td key={key}>{colDef.render(row)}</td>;
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <Pagination
          currentPage={pagination.current_page}
          totalPages={pagination.total_pages}
          onPageChange={handlePageChange}
        />
      </main>
    </>
  );
}

export default SuppliersPage;
